<template>
  <div class="metrics">
    <div class="filter">
      <sui-button-group size="tiny">
        <sui-button :class="{blue: period === 'hour'}" data-goal="METRICS_HOUR" @click="period = 'hour'">last hour</sui-button>
        <sui-button :class="{blue: period === 'day'}" :disabled="account.limits.metricsRetention === 'hour'" data-goal="METRICS_DAY" @click="period = 'day'">last day</sui-button>
        <sui-button :class="{blue: period === 'week'}" :disabled="account.limits.metricsRetention === 'hour' || account.limits.metricsRetention === 'day'" data-goal="METRICS_WEEK" @click="period = 'week'">last week</sui-button>
        <sui-button :class="{blue: period === 'month'}" :disabled="account.limits.metricsRetention !== 'month'" data-goal="METRICS_MONTH" @click="period = 'month'">last month</sui-button>
      </sui-button-group>
      &nbsp;
      <sui-button-group size="tiny">
        <sui-button :class="{blue: columns === 2}" icon="block layout" @click="columns = 2" title="Small charts" data-goal="METRICS_SMALL" />
        <sui-button :class="{blue: columns === 1}" icon="sidebar" @click="columns = 1" title="Wide charts" data-goal="METRICS_WIDE" />
      </sui-button-group>
      <sui-button floated="right" size="tiny" icon="sync" label-position="left" @click="updateStats()">Update</sui-button>
    </div>
    <br>
    <sui-grid class="charts" :columns="columns">
      <sui-grid-row>
        <sui-grid-column class="block" :width="16 / columns" v-if="currentStats.la">
          <sui-segment>
            <sui-label icon="chart area" attached="top">
              LA:
              <span v-if="currentStats.la.time">
                {{currentStats.la.load1}}&nbsp;&nbsp;{{currentStats.la.load5}}&nbsp;&nbsp;{{currentStats.la.load15}}
              </span>
              <span v-if="!currentStats.la.time">
                ?
              </span>
            </sui-label>
            <ChartLA class="chart" :data="series.la" />
          </sui-segment>
        </sui-grid-column>
        <sui-grid-column class="block" :width="16 / columns" v-if="currentStats.cpu">
          <sui-segment>
            <sui-label icon="chart area" attached="top">
              CPU:
              <span v-if="currentStats.cpu.time">
                {{currentCPU}}
              </span>
              <span v-if="!currentStats.cpu.time">
                ?
              </span>
            </sui-label>
            <ChartCPU class="chart" :data="series.cpu" />
          </sui-segment>
        </sui-grid-column>
        <sui-grid-column class="block" :width="16 / columns" v-if="currentStats.mem">
          <sui-segment>
            <sui-label icon="chart area" attached="top">
              Memory:
              <span v-if="currentStats.mem.time">
                {{prettyBytes(currentStats.mem.used)}} / {{prettyBytes(currentStats.mem.total)}}
              </span>
              <span v-if="!currentStats.mem.time">
                ?
              </span>
            </sui-label>
            <ChartMemory class="chart" :data="series.mem" />
          </sui-segment>
        </sui-grid-column>
        <sui-grid-column class="block" :width="16 / columns" v-if="currentStats.main_disk">
          <sui-segment>
            <sui-label icon="chart area" attached="top">
              <span v-if="currentStats.main_disk.time">
                Main Disk: {{prettyBytes(currentStats.main_disk.used)}} / {{prettyBytes(currentStats.main_disk.total)}}
                &nbsp;&nbsp;
                <span v-if="currentStats.ebs_disk">
                  EBS Disk: {{prettyBytes(currentStats.ebs_disk.used)}} / {{prettyBytes(currentStats.ebs_disk.total)}}
                </span>
              </span>
              <span v-if="!currentStats.main_disk.time">
                Disk: ?
              </span>
            </sui-label>
            <ChartDisk class="chart" :data="series.disk" />
          </sui-segment>
        </sui-grid-column>
      </sui-grid-row>
    </sui-grid>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import ChartCPU from '@/components/charts/CPU'
  import ChartLA from '@/components/charts/LA'
  import ChartMemory from '@/components/charts/Memory'
  import ChartDisk from '@/components/charts/Disk'

  const periods = {
    month: 30 * 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
  }
  const steps = {
    month: 60 * 60 * 1000,
    week: 20 * 60 * 1000,
    day: 5 * 60 * 1000,
    hour: 60 * 1000,
  }

  function getTime(t = null) {
    return (new Date(t)).getTime()
  }

  export default {
    name: 'Metrics',
    components: {
      ChartCPU,
      ChartLA,
      ChartMemory,
      ChartDisk,
    },
    props: {
      entity: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        period: 'hour',
        columns: 2,
        series: {},
      }
    },
    computed: {
      ...mapGetters(['account']),
      currentStats() {
        return this.entity._stats
      },
      coresCorrection() {
        return this.entity.target === 'container' ? this.entity._host._instanceData.cores : 1
      },
      currentCPU() {
        const value = (this.currentStats.cpu.used_percent / this.coresCorrection) || 0
        return value.toFixed(value < 1 ? 2 : (value < 10 ? 1 : 0)) + '%'
      },
    },
    watch: {
      period() {
        this.updateStats()
      },
    },
    sockets: {
      connect() {
        this.updateStats()
      },
      stats(data) {
        const sameId = data.id === this.entity.id
        const sameTarget = data.target === this.entity.target
        const stats = data.data

        if (sameId && sameTarget && this.period === 'hour') {
          const newStats = {}
          if (stats.cpu) {
            stats.cpu.used_percent = stats.cpu.used_percent / this.coresCorrection
            const t = getTime(stats.cpu.time)
            newStats.cpu = [
              [{t, y: stats.cpu.used_percent}],
            ]
          }
          if (stats.mem) {
            const t = getTime(stats.mem.time)
            newStats.mem = [
              [{t, y: stats.mem.total}],
              [{t, y: stats.mem.used}],
              [{t, y: stats.mem.buffered}],
              [{t, y: stats.mem.cached}],
            ]
          }
          if (stats.la) {
            const t = getTime(stats.la.time)
            newStats.la = [
              [{t, y: stats.la.load1}],
              [{t, y: stats.la.load5}],
              [{t, y: stats.la.load15}],
            ]
          }
          if (stats.main_disk) {
            const t = getTime(stats.main_disk.time)
            newStats.disk = [
              [{t, y: stats.main_disk.used}],
              [{t, y: stats.main_disk.total}],
            ]
          }
          if (stats.ebs_disk) {
            const t = getTime(stats.ebs_disk.time)
            newStats.disk.push(
              [{t, y: stats.ebs_disk.used}],
              [{t, y: stats.ebs_disk.total}]
            )
          }
          this.addStats(newStats)
        }
      },
    },
    methods: {
      updateStats() {
        this.entity.fetchStats({period: this.period}).then(data => {
          const stats = data.data
          const statsSince = getTime(data.since)
          const statsUntil = statsSince + periods[this.period]
          const step = steps[this.period]
          const series = {}
          // add current stats
          if (this.period === 'hour') {
            for (const field in stats) {
              if (stats[field] && this.currentStats[field]) {
                stats[field].push(this.currentStats[field])
              }
            }
          }
          if (stats.cpu) {
            series.cpu = [[]]
          }
          if (stats.la) {
            series.la = [[], [], []]
          }
          if (stats.mem) {
            series.mem = (stats.mem[0] && stats.mem[0].cached) ? [[], [], [], []] : [[], []]
          }
          if (stats.main_disk) {
            series.disk = this.currentStats.ebs_disk ? [[], [], [], []] : [[], []]
          }
          for (let t = statsSince; t < statsUntil; t += step) {
            if (this.currentStats.cpu && stats.cpu) {
              const closestItem = stats.cpu.find(item => Math.abs(getTime(item.time) - t) < 1.4 * step)
              series.cpu[0].push({t, y: closestItem ? closestItem.used_percent / this.coresCorrection : null})
            }
            if (this.currentStats.la && stats.la) {
              const closestItem = stats.la.find(item => Math.abs(getTime(item.time) - t) < 1.4 * step)
              series.la[0].push({t, y: closestItem ? closestItem.load1 : null})
              series.la[1].push({t, y: closestItem ? closestItem.load5 : null})
              series.la[2].push({t, y: closestItem ? closestItem.load15 : null})
            }
            if (this.currentStats.mem && stats.mem) {
              const closestItem = stats.mem.find(item => Math.abs(getTime(item.time) - t) < 1.4 * step)
              series.mem[0].push({t, y: closestItem ? closestItem.total : null})
              series.mem[1].push({t, y: closestItem ? closestItem.used : null})
              if (series.mem[2]) {
                series.mem[2].push({t, y: closestItem ? closestItem.buffered : null})
              }
              if (series.mem[3]) {
                series.mem[3].push({t, y: closestItem ? closestItem.cached : null})
              }
            }
            if (this.currentStats.main_disk && stats.main_disk) {
              const closestItem = stats.main_disk.find(item => Math.abs(getTime(item.time) - t) < 1.4 * step)
              series.disk[0].push({t, y: closestItem ? closestItem.used : null})
              series.disk[1].push({t, y: closestItem ? closestItem.total : null})
            }
            if (this.currentStats.ebs_disk && stats.ebs_disk) {
              const closestItem = stats.ebs_disk.find(item => Math.abs(getTime(item.time) - t) < 1.4 * step)
              series.disk[2].push({t, y: closestItem ? closestItem.used : null})
              series.disk[3].push({t, y: closestItem ? closestItem.total : null})
            }
          }
          this.series = series
        })
      },
      addStats(newSeries) {
        for (const field in this.series) {
          this.series[field] = this.series[field].map((array, index) => {
            const newSeriesValues = newSeries[field] ? newSeries[field][index] : []
            return array.concat(newSeriesValues).slice(this.period === 'hour' ? -60 : 0)
          })
        }
      },
      addRandom() {
        const lastMem = this.series.mem[0][this.series.mem[0].length - 1]
        const minMem = Math.min(...this.series.mem[0].map(mem => mem.y))
        const t = (lastMem.t || getTime()) + steps[this.period]
        const newStats = {
          cpu: [
            [{t, y: Math.round(Math.random() * 1000) / 100}],
          ],
          mem: [
            [{t, y: minMem * 2}],
            [{t, y: Math.round(Math.random() * 100000000 + 700000000)}],
            [{t, y: Math.round(Math.random() * 5000000 + 30000000)}],
            [{t, y: Math.round(Math.random() * 5000000 + 70000000)}],
          ],
        }
        if (this.series.disk) {
          const minDisk0 = Math.min(...this.series.disk[0].map(disk => disk.y))
          const minDisk1 = Math.min(...this.series.disk[1].map(disk => disk.y))
          Object.assign(newStats, {
            la: [
              [{t, y: Math.round(Math.random() / 2 * 100) / 100}],
              [{t, y: Math.round(Math.random() / 10 * 100) / 100}],
              [{t, y: Math.round(Math.random() / 30 * 100) / 100}],
            ],
            disk: [
              [{t, y: minDisk0 + Math.round(Math.random() * 138746828)}],
              [{t, y: minDisk1}],
            ],
          })
          if (this.currentStats.ebs_disk) {
            const lastDisk2 = this.series.disk[2][this.series.mem[0].length - 1].y
            const minDisk3 = Math.min(...this.series.disk[3].map(disk => disk.y))
            newStats.disk.push(
              [{t, y: lastDisk2 + Math.round(Math.random() * 1042061568)}],
              [{t, y: minDisk3 * 2}]
            )
          }
        }
        this.addStats(newStats)
      },
    },
    mounted() {
      this.updateStats()
      this.entity.subscribeStats()
    },
    beforeDestroy() {
      this.series = {}
      this.entity.unsubscribeStats()
    },
  }
</script>

<style scoped>
  .charts { margin-bottom: -1em!important; }
  .block { margin-bottom: 1em!important; }
  .one.column .chart { height: 330px; }
  .two.column .chart { height: 270px; }
</style>
